import { useEffect } from 'react';
import i18n from '@/i18n';
import type { PageMeta } from '@/types';

const upsertMeta = (
  selector: string,
  tagName: 'meta' | 'link',
  attributes: Record<string, string>
) => {
  let element = document.head.querySelector(selector) as HTMLElement | null;
  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element!.setAttribute(key, value);
  });
};

const removeHreflang = () => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
};

const addHreflang = (canonicalPath: string) => {
  removeHreflang();
  const origin = window.location.origin;
  const supportedLangs = ['en', 'zh'];
  
  supportedLangs.forEach(lng => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lng);
    link.setAttribute('href', `${origin}${canonicalPath}`);
    document.head.appendChild(link);
  });
  
  // x-default
  const defaultLink = document.createElement('link');
  defaultLink.setAttribute('rel', 'alternate');
  defaultLink.setAttribute('hreflang', 'x-default');
  defaultLink.setAttribute('href', `${origin}${canonicalPath}`);
  document.head.appendChild(defaultLink);
};

export function usePageMeta(meta: PageMeta | null) {
  useEffect(() => {
    if (!meta) return;

    const origin = window.location.origin;
    const canonicalUrl = `${origin}${meta.canonicalPath}`;
    const imageUrl = meta.image.startsWith('http') ? meta.image : `${origin}${meta.image}`;

    document.title = meta.title;

    upsertMeta('meta[name="description"]', 'meta', { name: 'description', content: meta.description });
    upsertMeta('meta[name="robots"]', 'meta', { name: 'robots', content: meta.robots });
    upsertMeta('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonicalUrl });
    upsertMeta('meta[property="og:title"]', 'meta', { property: 'og:title', content: meta.title });
    upsertMeta('meta[property="og:description"]', 'meta', { property: 'og:description', content: meta.description });
    upsertMeta('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', 'meta', { property: 'og:image', content: imageUrl });
    
    // Add language meta
    upsertMeta('meta[http-equiv="content-language"]', 'meta', { 'http-equiv': 'content-language', content: i18n.language });
    
    // Add hreflang tags
    addHreflang(meta.canonicalPath);
  }, [meta]);
}
